import { notifyApiErrors } from '@shared-core/runtime/api-errors'
import { getApiHealthHandler } from '@shared-core/runtime/api-health'
import { getApiPlatform } from '@shared-core/runtime/api-platform'
import { getAuthRefreshHandler } from '@shared-core/runtime/auth-refresh'
import { getAuthToken } from '@shared-core/runtime/auth-token'
import { getCurrentBaseUrl } from '@shared-core/services/api/env-manager'
import { getSessionAbortSignal } from '@shared-core/services/api/session-abort'
import { createBurstDetector } from '@shared-core/utils/burst-detector'
import { getRequestHeaderFromConfig } from '@shared-core/utils/request-header'
import axios, { AxiosError, type AxiosRequestConfig, type GenericAbortSignal } from 'axios'
import type { z } from 'zod'
import {
  API_KNOWN_ERRORS,
  API_STATUS_ERROR,
  API_STATUS_INVALID_TOKEN,
  getKnownErrorByHttpStatus,
  HTTP_RESPONSE_CODE,
} from './configs/api'

type Method = 'get' | 'post' | 'put' | 'patch' | 'delete'
type DebugLevel = 'info' | 'error' | 'disabled'
type Config<P> = Omit<AxiosRequestConfig, 'method' | 'url' | 'params'> & P

type Options<T> = {
  silentErrorCodes?: readonly string[]
  silentErrorStatuses?: readonly number[]
  debug?: DebugLevel
  isLoggerEnabled?: boolean
  method: Method
  parser: T
  url: string
}

export const api = axios.create({
  timeout: 60000,
})

const mergeAbortSignals = (
  requestSignal?: GenericAbortSignal,
  sessionSignal?: AbortSignal,
): GenericAbortSignal | undefined => {
  if (!requestSignal) return sessionSignal

  if (!sessionSignal || requestSignal === sessionSignal) return requestSignal

  if (requestSignal.aborted || sessionSignal.aborted) {
    const controller = new AbortController()
    controller.abort()
    return controller.signal
  }

  const controller = new AbortController()
  const abort = (): void => {
    controller.abort()
    requestSignal.removeEventListener?.('abort', abort)
    sessionSignal.removeEventListener?.('abort', abort)
  }

  requestSignal.addEventListener?.('abort', abort, { once: true })
  sessionSignal.addEventListener?.('abort', abort, { once: true })

  return controller.signal
}

// Безопасность: baseURL устанавливается per-request, чтобы избежать устаревшего значения при смене env
api.interceptors.request.use((config) => {
  config.baseURL = getCurrentBaseUrl()
  config.signal = mergeAbortSignals(config.signal, getSessionAbortSignal())

  const platform = getApiPlatform()

  const token = getAuthToken()

  if (token && config.url !== '/api/v1/auth/tma') {
    config.headers.set('Authorization', `Bearer ${token}`)
  }

  if (platform) {
    config.headers.set('x-app-platform', platform)
  }

  return config
})

let refreshInFlight: Promise<boolean> | null = null

const transportBurst = createBurstDetector({ threshold: 4, minWindowMs: 3_000, streakThreshold: 6, hardThreshold: 2 })

export const resetTransportFailures = (): void => transportBurst.reset()

// Бэк отвечает, но 5xx - сервис так же недоступен, как и при обрыве сети.
const isServerError = (error: AxiosError): boolean => (error.response?.status ?? 0) >= HTTP_RESPONSE_CODE.SERVER_ERROR

const isTransportFailure = (error: AxiosError): boolean => {
  // Отменённые запросы (смена сессии, размонтирование, dedupe) инициированы приложением,
  // а не сетью. Иначе resetSessionAbortController() кормит burst-детектор ложными сбоями
  // и на хорошем интернете всплывает штора «сервис недоступен».
  if (axios.isCancel(error)) {
    return false
  }

  if (!error.response) {
    return true
  }

  if (isServerError(error)) {
    return true
  }

  return (
    error.code === AxiosError.ECONNABORTED ||
    error.code === AxiosError.ETIMEDOUT ||
    error.code === AxiosError.ERR_NETWORK
  )
}

const reportTransportSuccess = (): void => {
  if (transportBurst.onSuccess()) {
    getApiHealthHandler()?.(false)
  }
}

const reportTransportFailure = (isServerFailure: boolean): void => {
  if (transportBurst.onFailure(isServerFailure)) {
    getApiHealthHandler()?.(true)
  }
}

const isInvalidTokenResponse = (error: AxiosError) => {
  const status = error.response?.status
  const data = error.response?.data as { status?: string } | undefined
  return status === HTTP_RESPONSE_CODE.UNAUTHORIZED || data?.status === API_STATUS_INVALID_TOKEN
}

function shouldSkipAuthRefresh(config?: AxiosRequestConfig): boolean {
  const url = config?.url ?? ''

  // refresh_tokens с 401 не должен повторно вызывать refresh — иначе deadlock на refreshInFlight.
  return url.includes('/auth/refresh_tokens') || url === '/api/v1/auth/tma'
}

function shouldSkipTransportBurst(config?: AxiosRequestConfig): boolean {
  const url = config?.url ?? ''

  // Проверка доступности сервисов ходит в /config/global — не считаем это global degradation.
  return url.includes('/config/global')
}

// Кулдаун на повторный тост известной ошибки. Фоновые запросы (баланс/курсы) при 429 сыплют
// TOO_MANY_REQUESTS пачками — без throttle тост пересоздаётся по кругу и «не закрывается».
// 15с — середина комфортного диапазона 10–30с, правится одной константой.
const KNOWN_ERROR_TOAST_COOLDOWN_MS = 15_000
const lastKnownErrorNotifyAt = new Map<string, number>()

const shouldNotifyKnownError = (knownError: string): boolean => {
  const now = Date.now()
  const last = lastKnownErrorNotifyAt.get(knownError) ?? 0

  if (now - last < KNOWN_ERROR_TOAST_COOLDOWN_MS) {
    return false
  }

  lastKnownErrorNotifyAt.set(knownError, now)
  return true
}

api.interceptors.response.use(
  (response) => {
    reportTransportSuccess()

    return response
  },
  // Перехватываем ошибки сети чтобы отследить состояние соединения
  async (error: AxiosError) => {
    if (isTransportFailure(error) && !shouldSkipTransportBurst(error.config)) {
      reportTransportFailure(isServerError(error))
    }

    const originalRequest = error.config

    if (!originalRequest || !isInvalidTokenResponse(error) || shouldSkipAuthRefresh(originalRequest)) {
      return Promise.reject(error)
    }

    if ((originalRequest as { __isRetry?: boolean }).__isRetry) {
      return Promise.reject(error)
    }

    const refreshHandler = getAuthRefreshHandler()

    if (!refreshHandler) {
      return Promise.reject(error)
    }

    try {
      if (!refreshInFlight) {
        refreshInFlight = refreshHandler()
      }
      const refreshed = await refreshInFlight
      refreshInFlight = null

      if (!refreshed) {
        return Promise.reject(error)
      }

      ;(originalRequest as { __isRetry?: boolean }).__isRetry = true
      return api(originalRequest)
    } catch (refreshError) {
      refreshInFlight = null
      return Promise.reject(refreshError as Error)
    }
  },
)

const patchConfigAndURL = <P>(url: string, config?: Config<P>): [string, AxiosRequestConfig | undefined] => {
  if (!config) {
    return [url, undefined]
  }

  if (!('routeParams' in config)) {
    return [url, config]
  }

  const { routeParams, ...rest } = config

  const params = routeParams as Record<string, unknown>
  const keys = Object.keys(params).sort((a, b) => b.localeCompare(a))

  const patchedURL = keys.reduce((acc, key) => {
    acc = acc.replace(`:${key}`, params[key] as string)
    return acc
  }, url)

  return [patchedURL, rest]
}

// Единый конверт ошибки бэка (валидация 422, бизнес-ошибки 4xx/5xx). handleErrors умеет его читать.
type ApiErrorEnvelope = {
  errors?: Record<string, unknown>
  status?: string | null
  message?: string | null
  meta?: Record<string, unknown> | null
}

const isApiErrorEnvelope = (value: unknown): value is ApiErrorEnvelope =>
  typeof value === 'object' && value !== null && ('errors' in value || 'status' in value)

const reportApiError = (response: ApiErrorEnvelope): void => {
  const codes = Object.values(response.errors ?? {}).flat()
  const rateLimitCodes = [API_KNOWN_ERRORS.RATE_LIMIT_EXCEEDED, API_KNOWN_ERRORS.TOO_MANY_REQUESTS]

  if (codes.length > 0 && codes.every((code) => rateLimitCodes.some((item) => item === code))) {
    if (!shouldNotifyKnownError(API_KNOWN_ERRORS.RATE_LIMIT_EXCEEDED)) return
  }
  notifyApiErrors(response)
}

const reportHttpError = (status?: number): void => {
  reportApiError({
    errors: { base: [getKnownErrorByHttpStatus(status ?? 0) ?? API_KNOWN_ERRORS.REQUEST_ERROR] },
    status: API_STATUS_ERROR,
  })
}

// Сервер отменил операцию (напр. исчерпаны попытки безопасности) — флоу должен увести назад.
export const isOperationCanceled = (res: unknown): boolean =>
  isApiErrorEnvelope(res) && res.meta?.operationCanceled === true

// Auth-статусы ведёт response-интерцептор (refresh/logout) — их тело наружу не отдаём.
const isAuthErrorStatus = (status: number | undefined): boolean =>
  status === HTTP_RESPONSE_CODE.UNAUTHORIZED || status === HTTP_RESPONSE_CODE.TOKEN_EXPIRED

// Параллельные одинаковые GET делят один in-flight Promise (один сетевой запрос).
const inFlightRequests = new Map<string, Promise<unknown>>()

// Единственный per-call header, который меняет смысл GET при одинаковых url/params: mini-apps SDK
// (apiGetMiniAppIntentById и др.) скоупит ответ по X-SDK-Session-Token. Auth и x-app-platform
// одинаковы для всех параллельных вызовов в сессии — их в ключ не кладём.
const SDK_SESSION_TOKEN_HEADER = 'X-SDK-Session-Token'

const getInFlightDedupeKey = (requestUrl: string, config?: AxiosRequestConfig): string =>
  JSON.stringify({
    url: requestUrl,
    params: config?.params ?? null,
    sdkSessionToken: getRequestHeaderFromConfig(config, SDK_SESSION_TOKEN_HEADER),
  })

export const makeAPI = <T extends z.ZodType, P = void>({
  method,
  parser,
  url,
  silentErrorCodes = [],
  silentErrorStatuses = [],
  isLoggerEnabled = true,
}: Options<T>) => {
  // Возвращаемый тип узкий (модель | null): вызовы читают res.data/res.meta напрямую.
  return (...args: P extends void ? [] : [config: Config<P>]): Promise<z.infer<T> | null> => {
    const config = args[0]
    const [patchedURL, patchedConfig] = patchConfigAndURL(url, config)

    const execute = async (): Promise<z.infer<T> | null> => {
      const sessionSignalOnStart = getSessionAbortSignal()

      const payload = { method, url: patchedURL, ...patchedConfig }

      try {
        const { data, status } = await api(payload)
        const isStaleSession = sessionSignalOnStart !== getSessionAbortSignal()

        if (isStaleSession || patchedConfig?.signal?.aborted) {
          return null
        }

        if (
          isApiErrorEnvelope(data) &&
          (data.status === API_STATUS_ERROR || Object.keys(data.errors ?? {}).length > 0)
        ) {
          const codes = Object.values(data.errors ?? {}).flat()

          if (
            !silentErrorStatuses.includes(status) &&
            !codes.some((code) => typeof code === 'string' && silentErrorCodes.includes(code))
          )
            reportApiError(data)
          return data as z.infer<T>
        }

        const result = parser.safeParse(data)

        if (result.error) {
          // Строковые ответы (HTML-страницы 404/500 и т.п.) не логируем: это не ошибка
          // контракта, а известный HTTP-статус — он обрабатывается ниже.
          if (__DEV__ && isLoggerEnabled && typeof data !== 'string') {
            // eslint-disable-next-line kartoy/functions-no-console
            console.log(patchedURL, result.error?.message, payload, data)
          }
        }

        // handle 404/429/500 errors
        if (typeof data === 'string') {
          if (status >= 400) reportHttpError(status)
          const knownError = getKnownErrorByHttpStatus(status)

          if (knownError) {
            return {
              errors: { base: [knownError] },
              status: API_STATUS_ERROR,
            } as z.infer<T>
          }

          return null
        }

        return result.success ? result.data : null
      } catch (error) {
        if (sessionSignalOnStart !== getSessionAbortSignal() || patchedConfig?.signal?.aborted) return null
        const isCanceled = axios.isCancel(error)

        if (isCanceled) {
          if (__DEV__) {
            // eslint-disable-next-line kartoy/functions-no-console
            if (isLoggerEnabled && error.code !== 'ERR_CANCELED') {
              console.log(error)
            }
          }

          return null
        }

        // 4xx/5xx с envelope-ошибкой (напр. 422 { errors: { email: ['EMAIL_ALREADY_EXISTS'] } }):
        // axios бросает — прокидываем тело дальше, чтобы handleErrors показал валидацию, а не молчал.
        if (axios.isAxiosError(error) && !isAuthErrorStatus(error.response?.status)) {
          const status = error.response?.status
          const body = error.response?.data

          // 413 часто HTML от nginx («Request Entity Too Large») — не envelope.
          // Нормализуем, чтобы фичи (materials и т.п.) могли показать лимит размера, а не generic.
          if (status === HTTP_RESPONSE_CODE.PAYLOAD_TOO_LARGE) {
            reportHttpError(status)
            return {
              errors: { base: ['request entity too large'] },
              message: 'Request Entity Too Large',
              status: API_STATUS_ERROR,
            } as z.infer<T>
          }

          if (isApiErrorEnvelope(body)) {
            const codes = Object.values(body.errors ?? {}).flat()

            if (
              !silentErrorStatuses.includes(status ?? 0) &&
              !codes.some((code) => typeof code === 'string' && silentErrorCodes.includes(code))
            )
              reportApiError(body)
            return body as z.infer<T>
          }

          if (!silentErrorStatuses.includes(status ?? 0)) reportHttpError(status)
        } else if (axios.isAxiosError(error) && !error.response) {
          reportHttpError()
        }

        return null
      }
    }

    // Dedup только для GET: чтение идемпотентно, параллельным caller'ам можно отдать один ответ
    // (поллинг accounts/all, required_actions и т.п.). POST/PUT/PATCH/DELETE — мутации: схлопывание
    // может проглотить повторное действие или сломаться на FormData/multipart.
    // Запрос с личным signal нельзя объединять: отмена одного экрана затронет другого потребителя.
    if (method !== 'get' || patchedConfig?.signal) {
      return execute()
    }

    const dedupeKey = getInFlightDedupeKey(patchedURL, patchedConfig)
    const existing = inFlightRequests.get(dedupeKey)

    if (existing) {
      return existing as Promise<z.infer<T> | null>
    }

    const request = execute()
    inFlightRequests.set(dedupeKey, request)
    void request.finally(() => {
      if (inFlightRequests.get(dedupeKey) === request) {
        inFlightRequests.delete(dedupeKey)
      }
    })

    return request
  }
}
